import React, { Fragment, useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { faAngleDoubleDown, faAngleLeft, faAngleRight, faClone, faFile, faPlay, faStop, faTrashAlt } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { calculateThumbDimensions } from "@/lib/canvas/utils";
import * as animationActions from "@/lib/state/actions/animation-actions";
import * as editorActions from "@/lib/state/actions/editor-actions";
import { AnimationFrame } from "@/lib/state/reducers/animation-default-state";
import { useAppDispatch } from "@/lib/state/store";

import Delay from "../delay";
import EditorButton from "../editor-button";
import TimePicker from "../time-picker";

import LayerSwitcher from "./layer-switcher";
import TimelineFrame from "./timeline-frame";
import { timelineSelector } from "./timeline-selectors";

import "./_timeline.scss";

type SequenceWithPlaceholders = (AnimationFrame | "placeholder" | "empty")[];

const Timeline = () => {
  const [timePickerVisible, setTimePickerVisible] = useState(false);
  const timelineRef = useRef(null);
  const containerRef = useRef(null);
  const dispatch = useAppDispatch();
  const state = useSelector(timelineSelector);

  const onDelayPressed = () => {
    setTimePickerVisible(true);
  };

  const toggleTimePicker = () => {
    setTimePickerVisible(false);
  };

  const onNextFramePressed = () => {
    dispatch(editorActions.nextFrame());
  };

  const onPreviousFramePressed = () => {
    dispatch(editorActions.previousFrame());
  };

  const onDelayChanged = (delayData: { applyAll: boolean; delay: number }) => {
    dispatch(
      animationActions.setFrameDelayAction(
        state.currentFrameIndex,
        delayData.delay,
        delayData.applyAll,
      ),
    );
    setTimePickerVisible(false);
  };

  const onAddFramePressed = () => {
    dispatch(
      animationActions.addFrame(
        state.currentFrameIndex + 1,
        state.sequence[state.currentFrameIndex].delay,
      ),
    );
    dispatch(editorActions.goToFrame(state.currentFrameIndex + 1));
  };

  const onFrameSelected = useCallback(
    (id: string) => {
      dispatch(editorActions.goToFrameId(id));
    },
    [dispatch],
  );

  const onDeleteFramePressed = () => {
    dispatch(animationActions.deleteFrame(state.currentFrameIndex));
    if (
      state.currentFrameIndex === state.sequence.length - 1 &&
      state.currentFrameIndex > 0
    ) {
      dispatch(editorActions.goToFrame(state.currentFrameIndex - 1));
    }
  };

  const onCopyFramePressed = () => {
    dispatch(editorActions.toggleFramePicker());
  };

  const onStartPlaybackPressed = () => {
    dispatch(editorActions.startPlayback());
  };

  const onStopPlaybackPressed = () => {
    dispatch(editorActions.stopPlayback());
  };

  // if (state.sequence.length === 0) return null;
  const { currentFrameIndex } = state;
  const sequence: SequenceWithPlaceholders = state.sequence.slice(0);

  if (state.framePickerVisible && state.framePickerMode === "insert") {
    sequence.splice(state.currentFrameIndex + 1, 0, "placeholder");
  }

  let componentsToRender = [];

  const { width, height } = calculateThumbDimensions(
    state.frameDimensions.width,
    state.frameDimensions.height,
    120,
    90,
  );

  for (let index = 0; index < 11; index++) {
    const position = currentFrameIndex - 5 + index;
    const sequenceItem = sequence[position];

    if (
      sequenceItem === "empty" ||
      position < 0 ||
      position > sequence.length - 1
    ) {
      componentsToRender.push(
        <li
          className="timeline__frame m-2 rounded-md border-2 border-dashed border-gray-300 bg-gray-200"
          key={`empty-${index}`}
          style={{ width, height }}
        />,
      );
    } else if (sequenceItem === "placeholder") {
      componentsToRender.push(
        <li
          className="timeline__frame m-2 rounded-md border-2 border-dashed border-gray-300 bg-gray-200"
          key="insert-placeholder"
          style={{ width, height }}
        >
          <div className="timeline__frame--insert">
            <FontAwesomeIcon icon={faAngleDoubleDown} />
          </div>
        </li>,
      );
    } else {
      componentsToRender.push(
        <TimelineFrame
          layers={sequenceItem.layers}
          index={position + 1}
          key={`${sequenceItem.id}-${index}`}
          id={sequenceItem.id}
          showCopyOverlay={
            (state.framePickerVisible &&
              state.framePickerMode === "overwrite-all") ||
            (state.framePickerVisible &&
              state.framePickerMode === "overwrite" &&
              index === 5)
          }
          onFrameSelected={onFrameSelected}
          active={index === 5}
          width={state.frameDimensions.width}
          height={state.frameDimensions.height}
          showCounter={true}
        />,
      );
    }
  }

  if (state.sequence.length === 0) return null;

  return (
    <Fragment>
      <TimePicker
        visible={timePickerVisible}
        onClosePressed={toggleTimePicker}
        onChange={onDelayChanged}
        currentValue={state.sequence[state.currentFrameIndex].delay}
      />
      <div
        className="timeline-container flex flex-col items-center"
        ref={containerRef}
      >
        <div className="timeline-toolbar">
          <LayerSwitcher />
          <EditorButton
            onButtonPressed={onDeleteFramePressed}
            transient={true}
            label={<FontAwesomeIcon icon={faTrashAlt} />}
            disabled={state.mode !== "editing"}
          />
          <div className="button-spacer" />
          <EditorButton
            onButtonPressed={onPreviousFramePressed}
            transient={true}
            label={<FontAwesomeIcon icon={faAngleLeft} />}
            disabled={state.mode !== "editing"}
          />
          <span className="frame-status-text">
            {state.currentFrameIndex + 1} / {state.sequence.length}
          </span>
          <EditorButton
            onButtonPressed={onNextFramePressed}
            transient={true}
            label={<FontAwesomeIcon icon={faAngleRight} />}
            disabled={state.mode !== "editing"}
          />
          <EditorButton
            onButtonPressed={onAddFramePressed}
            transient={true}
            label={<FontAwesomeIcon icon={faFile} />}
            disabled={state.mode !== "editing"}
          />
          <EditorButton
            onButtonPressed={onCopyFramePressed}
            transient={true}
            label={<FontAwesomeIcon icon={faClone} />}
            disabled={state.mode !== "editing"}
          />
          <div className="button-spacer" />
          <EditorButton
            onButtonPressed={onStartPlaybackPressed}
            transient={true}
            label={<FontAwesomeIcon icon={faPlay} />}
            disabled={state.mode !== "editing"}
          />
          <EditorButton
            onButtonPressed={onStopPlaybackPressed}
            transient={true}
            label={<FontAwesomeIcon icon={faStop} />}
          />
          <Delay
            value={state.sequence[state.currentFrameIndex].delay}
            onChange={onDelayChanged}
            onClick={onDelayPressed}
          />
        </div>

        <ol className={`timeline timeline--${state.mode}`} ref={timelineRef}>
          {componentsToRender}
        </ol>
      </div>
    </Fragment>
  );
};

export default Timeline;
